import React, { useEffect, useState } from 'react';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import { IEventFormData } from '@/types/community-management.types';
import clsx from 'clsx';
import { I18N_LANGUAGES } from '@/i18n';
import { useIntl } from 'react-intl';
import { useTranslateEventContentMutation } from '@/services/CommunityManagementService';
import { useSnackbar } from 'notistack';
import { formatDisplayDateTime } from '@/utils/Date';
import { formatFinnishCoordinate } from '@/utils';

interface EventPreviewModalProps extends Omit<ModalProps, 'children'> {
  formData: Partial<IEventFormData>;
  source_language?: string;
  contentHasChanged?: boolean; // Indicates if content has been modified from original
  onTranslationsGenerated?: (
    translations: Record<string, Partial<IEventFormData>>
  ) => void;
}

interface LanguagePreviewProps {
  language: {
    code: string;
    label: string;
    flag: string;
  };
  formData: Partial<IEventFormData>;
  translatedContent?: Record<
    string,
    {
      title?: string;
      description?: string;
      location_text?: string;
    }
  >;
  isLoading: boolean;
  onEditContent?: (
    languageCode: string,
    field: keyof EditValues,
    value: string
  ) => void;
  isEditable?: boolean;
}

// Interface for translation payload
interface TranslationPayload {
  title?: string;
  description?: string;
  location_text?: string;
  source_language: string;
  event_id?: string;
}

// Interface for edit values
interface EditValues {
  title?: string;
  description?: string;
  location_text?: string;
}

const FIELD_LABELS = {
  en: {
    title: 'Title',
    description: 'Description',
    location_text: 'Location',
  },
  de: {
    title: 'Titel',
    description: 'Beschreibung',
    location_text: 'Standort',
  },
  fi: {
    title: 'Otsikko',
    description: 'Kuvaus',
    location_text: 'Sijainti',
  },
  sv: {
    title: 'Titel',
    description: 'Beskrivning',
    location_text: 'Plats',
  },
} as const;

const LanguagePreview: React.FC<LanguagePreviewProps> = ({
  language,
  formData,
  translatedContent,
  isLoading,
  onEditContent,
  isEditable = true,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<EditValues>({});

  const fieldLabels =
    FIELD_LABELS[language.code as keyof typeof FIELD_LABELS] || FIELD_LABELS.en;

  const langContent =
    translatedContent && translatedContent[language.code]
      ? translatedContent[language.code]
      : formData;

  const toggleEditMode = () => {
    if (!isEditable) return;
    if (!isEditing) {
      const initialValues: EditValues = {};
      if (langContent?.title) initialValues.title = langContent.title;
      if (langContent?.description)
        initialValues.description = langContent.description;
      if (langContent?.location_text)
        initialValues.location_text = langContent.location_text;
      setEditValues(initialValues);
    }
    setIsEditing(!isEditing);
  };

  const handleSave = () => {
    if (onEditContent) {
      Object.entries(editValues).forEach(([field, value]) => {
        onEditContent(language.code, field as keyof EditValues, value);
      });
      setIsEditing(false);
    }
  };

  const handleChange = (field: keyof EditValues, value: string) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (langContent) {
      const initialValues: EditValues = {};
      if (langContent.title) initialValues.title = langContent.title;
      if (langContent.description)
        initialValues.description = langContent.description;
      if (langContent.location_text)
        initialValues.location_text = langContent.location_text;
      setEditValues(initialValues);
    }
  }, [langContent]);

  return (
    <div className="bg-gray-100 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden mb-5 border border-gray-200">
      <div className="bg-gray-200 px-5 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center">
          <img
            src={language.flag}
            alt={language.label}
            className="w-6 h-6 mr-3 rounded-sm shadow-sm"
          />
          <h3 className="text-base font-medium text-gray-800 flex items-center">
            {language.label}
            {isLoading && (
              <span className="ml-2 flex items-center text-xs text-blue-600 contrast:text-blue-200 font-medium">
                <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                translating...
              </span>
            )}
          </h3>
        </div>

        {/* Single Edit Icon for all fields */}
        {!isLoading && onEditContent && isEditable && (
          <button
            onClick={toggleEditMode}
            className={`${isEditing
                ? 'bg-blue-100 dark:bg-blue-900'
                : 'bg-gray-100 dark:bg-gray-400'
              } text-blue-600 dark:text-blue-200 hover:text-blue-800 p-1.5 rounded-full hover:bg-blue-50 dark:hover:bg-blue-600 transition-colors`}
            title={isEditing ? 'Cancel editing' : 'Edit all fields'}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="p-5 flex justify-center">
          <div className="animate-pulse flex space-x-4 w-full">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-5">
          {isEditing ? (
            <>
              {/* Edit Mode View */}
              <div className="space-y-6">
                {/* Title Field */}
                <div className="col-span-1">
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {fieldLabels.title}
                  </p>
                  <input
                    type="text"
                    value={editValues.title || ''}
                    onChange={(e) => handleChange('title', e.target.value)}
                    className="w-full input p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                    placeholder="Enter title"
                    autoComplete="off"
                  />
                </div>
                {/* Description Field */}
                <div className="col-span-2">
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {fieldLabels.description}
                  </p>
                  <textarea
                    value={editValues.description || ''}
                    onChange={(e) =>
                      handleChange('description', e.target.value)
                    }
                    className="w-full textarea p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-base min-h-[100px]"
                    placeholder="Enter description"
                  />
                </div>
                {/* Save Button */}
                <div className="flex justify-end mt-4">
                  <button
                    onClick={handleSave}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center"
                    title="Save all changes"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Save Changes
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Normal View Mode */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Title Field */}
                <div className="col-span-1">
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {fieldLabels.title}
                  </p>
                  <p className="text-base font-medium text-gray-800">
                    {langContent?.title || '-'}
                  </p>
                </div>

                {/* Description Field */}
                <div className="col-span-2">
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {fieldLabels.description}
                  </p>
                  <p className="text-base text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {langContent?.description || '-'}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const EventPreviewModal: React.FC<EventPreviewModalProps> = ({
  formData,
  source_language,
  open,
  onClose,
  className,
  contentHasChanged = false,
  onTranslationsGenerated,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [translateEventContent, { isLoading }] =
    useTranslateEventContentMutation();
  const [translations, setTranslations] = useState<
    Record<
      string,
      {
        title?: string;
        description?: string;
        location_text?: string;
      }
    >
  >({});
  const [hasTranslated, setHasTranslated] = useState(false);
  const [lastTranslatedContent, setLastTranslatedContent] = useState<{
    title?: string;
    description?: string;
    location_text?: string;
  } | null>(null);

  // Use the contentHasChanged prop from parent, or fall back to internal tracking
  const contentModified = contentHasChanged;

  // Reset hasTranslated when content is modified
  useEffect(() => {
    if (contentModified) {
      setHasTranslated(false);
    }
  }, [contentModified]);

  const ensureAllLanguages = (
    translations: Record<string, Partial<IEventFormData>>
  ) => {
    const completeTranslations: Record<string, Partial<IEventFormData>> = {};

    I18N_LANGUAGES.forEach((lang) => {
      if (translations[lang.code]) {
        completeTranslations[lang.code] = translations[lang.code];
      } else {
        completeTranslations[lang.code] = {
          title: formData.title || '',
          description: formData.description || '',
          location_text: formData.location_text || '',
        };
      }
    });

    return completeTranslations;
  };

  const handleEditContent = (
    languageCode: string,
    field: keyof EditValues,
    value: string
  ) => {
    setTranslations((prev) => {
      const newTranslations = { ...prev };

      if (!newTranslations[languageCode]) {
        newTranslations[languageCode] = {};
      }

      newTranslations[languageCode] = {
        ...newTranslations[languageCode],
        [field]: value,
      };

      // Ensure all languages are present before sending
      const completeTranslations = ensureAllLanguages(newTranslations);

      if (onTranslationsGenerated) {
        onTranslationsGenerated(completeTranslations);
      }

      return newTranslations;
    });
  };

  useEffect(() => {
    const fetchTranslations = async () => {
      if (!open) return;

      const baseSnapshot = {
        title: formData.title || '',
        description: formData.description || '',
        location_text: formData.location_text || '',
      };

      // If we already have translations for the current content, reuse them
      if (
        lastTranslatedContent &&
        lastTranslatedContent.title === baseSnapshot.title &&
        lastTranslatedContent.description === baseSnapshot.description &&
        lastTranslatedContent.location_text === baseSnapshot.location_text &&
        Object.keys(translations).length > 0
      ) {
        if (onTranslationsGenerated) {
          const completeTranslations = ensureAllLanguages(translations);
          onTranslationsGenerated(completeTranslations);
        }
        return;
      }

      if (
        !hasTranslated &&
        formData &&
        formData.translations &&
        !contentModified
      ) {
        const existingTranslations = formData.translations as Record<
          string,
          Partial<IEventFormData>
        >;

        if (Object.keys(existingTranslations).length > 0) {
          const completeTranslations = ensureAllLanguages(existingTranslations);
          setTranslations(completeTranslations);
          setHasTranslated(true);
          setLastTranslatedContent(baseSnapshot);

          if (onTranslationsGenerated) {
            onTranslationsGenerated(completeTranslations);
          }

          return;
        }
      }

      if (hasTranslated && formData.id && !contentModified) {
        if (onTranslationsGenerated) {
          const completeTranslations = ensureAllLanguages(translations);
          onTranslationsGenerated(completeTranslations);
        }
        return;
      }

      try {
        const dataToTranslate: TranslationPayload = {
          title: formData.title,
          description: formData.description,
          source_language: source_language || 'en',
          ...(formData.id && !contentModified ? { event_id: formData.id } : {}),
        };

        const response = await translateEventContent(dataToTranslate).unwrap();

        const warningMessage = response?.warning_message;
        if (warningMessage?.trim()) {
          enqueueSnackbar(warningMessage, { variant: 'warning' });
        }

        if (response.translations) {
          setTranslations(response.translations);
          setHasTranslated(true);

          const completeTranslations = ensureAllLanguages(
            response.translations
          );
          setLastTranslatedContent(baseSnapshot);

          if (onTranslationsGenerated) {
            onTranslationsGenerated(completeTranslations);
          }
        }
      } catch {
        enqueueSnackbar(
          intl.formatMessage({ id: 'EVENT.PREVIEW.TRANSLATION_ERROR' }) ||
          'Auto-translation is temporarily unavailable. Showing your entered text.',
          {
            variant: 'warning',
          }
        );
        const fallback = ensureAllLanguages({});
        setTranslations(fallback);
        if (onTranslationsGenerated) {
          onTranslationsGenerated(fallback);
        }
      }
    };

    fetchTranslations();
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx(
        'harbor-preview-root !flex items-center justify-center',
        className
      )}
      aria-labelledby="preview-modal-title"
      aria-describedby="preview-modal-description"
    >
      <div className="harbor-preview-modal bg-gray-100 w-full max-w-4xl mx-4 rounded-xl shadow-xl flex flex-col overflow-y-auto max-h-[560px] custom-scrollbar">
        {/* Header */}
        <div className="modal-header bg-gray-200 px-6 py-4 border-b border-gray-100">
          <div className="modal-header-row flex justify-between w-full items-center ">
            <h2
              id="preview-modal-title"
              className="text-xl font-bold text-gray-800 flex items-center"
            >
              <svg
                className="w-5 h-5 mr-2 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              {intl.formatMessage({ id: 'EVENT.PREVIEW.TITLE' }) ||
                'Event Preview'}
            </h2>
            <button
              type="button"
              onClick={() => onClose?.({}, 'backdropClick')}
              className="modal-close text-gray-400 hover:text-gray-600 focus:outline-none p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label={
                intl.formatMessage({ id: 'EVENT.BUTTON.CLOSE' }) || 'Close'
              }
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Event Details Section */}
          <div className="bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 rounded-xl p-4 mb-6 flex flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center mb-3">
                <svg
                  className="w-5 h-5 text-blue-600 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <h3 className="font-semibold text-gray-800">Event Schedule</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {intl.formatMessage({ id: 'EVENT.FORM.START_DATE' }) ||
                      'Start Date'}
                  </p>
                  <p className="text-base font-medium text-gray-800 mt-1">
                    {formData.start_at
                      ? formatDisplayDateTime(new Date(formData.start_at))
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {intl.formatMessage({ id: 'EVENT.FORM.END_DATE' }) ||
                      'End Date'}
                  </p>
                  <p className="text-base font-medium text-gray-800 mt-1">
                    {formData.end_at
                      ? formatDisplayDateTime(new Date(formData.end_at))
                      : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Location coordinates */}
            {(formData.latitude !== undefined ||
              formData.longitude !== undefined) && (
                <div className="flex-1 min-w-[200px] ml-4">
                  <div className="flex items-center mb-3">
                    <svg
                      className="w-5 h-5 text-blue-600 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <h3 className="font-semibold text-gray-800">Coordinates</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {intl.formatMessage({ id: 'EVENT.FORM.LATITUDE' }) ||
                          'Latitude'}
                      </p>
                      <p className="text-base font-medium text-gray-800 mt-1">
                        {formData.latitude !== undefined &&
                          formData.latitude !== null
                          ? formatFinnishCoordinate(formData.latitude)
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {intl.formatMessage({ id: 'EVENT.FORM.LONGITUDE' }) ||
                          'Longitude'}
                      </p>
                      <p className="text-base font-medium text-gray-800 mt-1">
                        {formData.longitude !== undefined &&
                          formData.longitude !== null
                          ? formatFinnishCoordinate(formData.longitude)
                          : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

            {/* Event image */}
            {formData.image && (
              <div className="flex gap-2 items-center ml-auto mt-3 sm:mt-0">
                <div className="w-16 h-16 rounded-md overflow-hidden shadow-sm">
                  <img
                    src={formData.image}
                    alt="Event preview"
                    className="w-full h-full object-cover transition-transform hover:scale-110"
                  />
                </div>
              </div>
            )}
          </div>

          <p className="text-sm text-gray-500 mb-4 flex items-center">
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {intl.formatMessage({ id: 'EVENT.PREVIEW.DESCRIPTION' }) ||
              'Preview your event content in different languages. You can edit translated content if needed.'}
          </p>

          <div className="lang-list pr-2 custom-scrollbar">
            {I18N_LANGUAGES.map((lang) => (
              <LanguagePreview
                key={lang.code}
                language={lang}
                formData={formData}
                translatedContent={translations}
                isLoading={isLoading}
                onEditContent={handleEditContent}
                isEditable={lang.code !== (source_language || 'en')}
              />
            ))}
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-200 border-t border-gray-100">
          <div className="footer-actions-row flex justify-end">
            <button
              type="button"
              onClick={() => onClose?.({}, 'backdropClick')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors duration-200"
              aria-label={
                intl.formatMessage({ id: 'EVENT.BUTTON.CLOSE' }) || 'Close'
              }
            >
              {intl.formatMessage({ id: 'EVENT.BUTTON.CLOSE' }) || 'Close'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EventPreviewModal;
